import { Button, Modal, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { HtmlReactParser, translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import { Check24Icon } from "@kaltura/ds-react-icons";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import styled from "@emotion/styled";

export interface ApproveProps {
    requestConfirmation: boolean;
    onApprove: () => void;
    loading: boolean;
    confirmationMessage: string;
    forceLargeButton?: boolean;
    isBulkAsync?: boolean;
    disabled?: boolean;
    report: (what: "act" | "act_confirm") => void;
}

const StyledButton = styled(Button)(({ children }) => ({
    ...(
        !children && {
            paddingLeft: 0,
            paddingRight: 0,
        }
    ),
}));

export function Approve({
    requestConfirmation,
    onApprove,
    forceLargeButton,
    loading,
    confirmationMessage,
    isBulkAsync,
    disabled,
    report,
}: ApproveProps) {
    const [showModal, setShowModal] = useState(false);

    const theme = useTheme();
    const isSmScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));
    const isXsScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));

    // show small button on xs screen or if forceLargeButton also on sm screen
    const showSmallButton = isXsScreen || (!forceLargeButton && isSmScreen);

    /**
     * "main button" click handler
     */
    const handleClick = () => {
        report("act");
        if (requestConfirmation || isBulkAsync) {
            setShowModal(true);
            return;
        }
        handleApprove();
    };

    const handleApprove = () => {
        setShowModal(false);
        report("act_confirm");
        onApprove();
    };

    return (
        <>
            <StyledButton
                color={"success"}
                variant={"pill"}
                startIcon={<Check24Icon />}
                onClick={handleClick}
                loading={loading}
                disabled={disabled}
                size={showSmallButton && !forceLargeButton ? "small" : "medium"}
            >
                {!showSmallButton && translate("Approve")}
            </StyledButton>
            <Modal open={showModal} onClose={() => setShowModal(false)}>
                <ModalTitle>{translate("Approve confirmation")}</ModalTitle>
                <ModalContent>{HtmlReactParser(confirmationMessage)}</ModalContent>
                <ModalActions>
                    <Button variant={"borderless"} color={"secondary"} onClick={() => setShowModal(false)}>
                        {translate("Cancel")}
                    </Button>
                    <Button color={"success"} onClick={handleApprove}>
                        {translate("Approve")}
                    </Button>
                </ModalActions>
            </Modal>
        </>
    );
}
